import { useEffect, useState } from 'preact/hooks'

import { api } from './api.js'

/**
 * Die Adressen der Profilbilder — einmal geholt, dann für alle da.
 *
 * ## Warum ein gemeinsamer Zwischenspeicher und kein Laden je Avatar
 *
 * Ein Avatar steht in der Familienliste, in der Chatliste, in jedem Chatkopf
 * und in der Anrufübersicht — auf einem Bildschirm leicht ein Dutzend Mal.
 * Holte jeder seine Adresse selbst, wären das ein Dutzend Netzaufrufe für
 * fünf verschiedene Bilder, jedes Mal neu beim Wechsel des Bereichs.
 *
 * Deshalb: eine Anfrage für alle IDs, die gerade gebraucht werden
 * (`POST /blobs/urls` kann das seit dem Fotoraster), und das Ergebnis liegt im
 * Speicher, bis die Seite neu geladen wird.
 *
 * ## Warum die Adressen ablaufen dürfen
 *
 * Sie sind signiert und an die Sitzung gebunden. Läuft eine ab, lädt das Bild
 * nicht mehr — und der Avatar zeigt wieder Initialen. Das ist der richtige
 * Ausfall: sichtbar, harmlos, und beim nächsten Laden der Seite behoben. Eine
 * Auffrischungslogik dafür wäre mehr Code als der Fehler wert ist.
 */

const adressen = new Map<string, string>()
/** Läuft gerade eine Anfrage für diese ID? Verhindert zehn gleiche Aufrufe. */
const unterwegs = new Map<string, Promise<void>>()

/** Was schon bekannt ist — ohne Netzaufruf, für die erste Anzeige. */
export function bekannteAdresse(blobId: string | null | undefined): string | undefined {
  return blobId ? adressen.get(blobId) : undefined
}

/**
 * Holt die fehlenden Adressen für diese IDs.
 *
 * Gibt zurück, ob etwas Neues dazugekommen ist — daran entscheidet der
 * Aufrufer, ob er sich neu zeichnen muss.
 */
export async function adressenLaden(blobIds: Array<string | null | undefined>): Promise<boolean> {
  const fehlend = [...new Set(
    blobIds.filter((id): id is string => !!id && !adressen.has(id) && !unterwegs.has(id)),
  )]
  if (fehlend.length === 0) {
    // Trotzdem auf das warten, was gerade unterwegs ist — sonst zeichnet der
    // Aufrufer zu früh und sieht wieder Initialen.
    await Promise.all([...unterwegs.values()])
    return false
  }

  const lauf = (async () => {
    try {
      // Der Server antwortet mit `{ urls: { <blobId>: <url> }, expiresAt }`.
      const antwort = await api.post<{ urls: Record<string, string> }>(
        '/blobs/urls', { blobIds: fehlend },
      )
      for (const [id, url] of Object.entries(antwort.urls)) adressen.set(id, url)
    } catch {
      // Kein Netz, keine Berechtigung, abgelaufen — dann bleiben die Initialen
      // stehen. Ein Profilbild ist nichts, wofür eine Liste scheitern darf.
    } finally {
      for (const id of fehlend) unterwegs.delete(id)
    }
  })()

  for (const id of fehlend) unterwegs.set(id, lauf)
  await lauf
  return true
}

/** Nach dem Ändern des eigenen Bildes: die alte Adresse gilt nicht mehr. */
export function vergessen(blobId: string | null | undefined): void {
  if (blobId) adressen.delete(blobId)
}

// --- Wer hat welches Bild ----------------------------------------------------

/**
 * Person → Blob-ID des Profilbildes.
 *
 * ## Warum zentral und nicht in jeder Datenquelle
 *
 * Avatare stehen auf der Familienseite (aus `/dashboard`), in der Chatliste und
 * im Chatkopf (aus `/family/members`), in der Anrufübersicht und beim
 * Befindlichkeitsstatus. Jede dieser Quellen hätte ein eigenes Feld gebraucht,
 * und jede hätte man einzeln vergessen können — dieselbe Fehlerklasse, die
 * dieses Projekt schon dreimal getroffen hat: gebaut, aber an einer Stelle
 * nicht angeschlossen.
 *
 * Eine Abfrage bei `/family/members`, eine Zuordnung, alle Avatare bedient.
 */
const bilder = new Map<string, string | null>()

/** Das Profilbild dieser Person — `undefined`, solange noch nichts geladen ist. */
export function bildVon(personId: string | null | undefined): string | null | undefined {
  return personId ? bilder.get(personId) : undefined
}

/**
 * Wer sitzt in diesem Raum gegenüber?
 *
 * Ein Direktchat hat kein eigenes Bild — er zeigt das der anderen Person, wie
 * man es von jedem Messenger kennt. Der Familienchat hat keine Person und
 * behält sein Symbol. Steht hier und nicht dreimal in den Routen, weil jede
 * Kopie eine Stelle wäre, an der das Bild später fehlt.
 */
export function gegenueber(
  kind: string,
  memberIds: readonly string[],
  meId: string,
): string | null {
  if (kind !== 'direct') return null
  return memberIds.find((id) => id !== meId) ?? null
}

/**
 * Lädt, wer welches Bild hat, und holt gleich die Adressen dazu.
 *
 * Läuft beim Start und nach jedem `family.changed`. Scheitert es, bleiben
 * Initialen stehen — ein Profilbild ist nichts, wofür ein Bildschirm leer
 * bleiben darf.
 */
export async function profilbilderLaden(): Promise<void> {
  try {
    const mitglieder = await api.get<Array<{ id: string; avatarBlobId: string | null }>>(
      '/family/members',
    )
    for (const m of mitglieder) bilder.set(m.id, m.avatarBlobId)
    await adressenLaden(mitglieder.map((m) => m.avatarBlobId))
  } catch {
    // Kein Netz, keine Sitzung — dann eben Initialen.
  } finally {
    // Auch nach einem Fehlschlag melden: Wer auf das Ergebnis wartet, soll
    // nicht hängen bleiben, sondern die Initialen sehen.
    bescheidGeben()
  }
}

// --- Bescheid sagen, wenn Bilder da sind -------------------------------------

/**
 * Warum eine eigene kleine Benachrichtigung und kein Store.
 *
 * Die Zuordnung oben ist eine `Map` im Modul — sie ändert sich, ohne dass
 * Preact davon erfährt. Ein Avatar, der beim ersten Zeichnen noch keine
 * Adresse hatte, bliebe deshalb für immer bei den Initialen, obwohl das Bild
 * längst da ist. Genau die Fehlerklasse, die dieses Projekt schon dreimal
 * getroffen hat: gebaut, geladen, aber nirgends angeschlossen.
 *
 * Ein Zähler, den jeder Avatar abonniert, reicht: Er ändert sich genau zweimal
 * je Sitzung (Start und nach `family.changed`), nicht je Nachricht.
 */
let stand = 0
const hoerer = new Set<(n: number) => void>()

function bescheidGeben(): void {
  stand += 1
  for (const h of hoerer) h(stand)
}

/** Zeichnet das Bauteil neu, sobald neue Profilbilder bekannt sind. */
export function useAvatare(): number {
  const [n, setN] = useState(stand)
  useEffect(() => {
    hoerer.add(setN)
    // Zwischen dem ersten Zeichnen und diesem Effekt kann etwas angekommen
    // sein — dann gleich nachziehen.
    setN(stand)
    return () => { hoerer.delete(setN) }
  }, [])
  return n
}
